import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuEye, LuTrash2, LuBadgeCheck, LuChevronDown, LuChevronUp, LuCheck, LuX } from "react-icons/lu";
import { authFetch, buildQuery, downloadFile } from "../../utils/api";
import { useToast, ConfirmDialog } from "./ui";

const PAGE_SIZE = 25;

const SORT_COLUMNS = {
  followers: "followers",
  name: "name",
  recent: "recent",
  verified: "verified",
};

function reviewBadge(status) {
  const s = String(status || "pending").toLowerCase();
  if (s === "qualified") return <span className="lf-badge lf-badge-green">Qualified</span>;
  if (s === "rejected") return <span className="lf-badge lf-badge-gray">Rejected</span>;
  return <span className="lf-badge lf-badge-blue">Pending</span>;
}

function initials(name, username) {
  return (name || username || "?").trim().slice(0, 1).toUpperCase();
}

function Avatar({ lead }) {
  const [failed, setFailed] = useState(false);
  if (lead.profile_image_url && !failed) {
    return (
      <img
        className="lf-avatar"
        src={lead.profile_image_url}
        alt={lead.username}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return <div className="lf-avatar-fallback">{initials(lead.name, lead.username)}</div>;
}

function LeadsTable({ onOpenLead, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState({
    hasEmail: false, hasPhone: false, hasWebsite: false, verified: false,
  });
  const [category, setCategory] = useState("");
  const [minFollowers, setMinFollowers] = useState("");
  const [maxFollowers, setMaxFollowers] = useState("");
  const [source, setSource] = useState("");
  const [reviewStatus, setReviewStatus] = useState("pending");

  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);

  const notify = useToast();
  const debounceRef = useRef(null);

  const queryParams = useMemo(() => ({
    page,
    pageSize: PAGE_SIZE,
    q: q || undefined,
    sortBy,
    sortDir,
    hasEmail: filters.hasEmail || undefined,
    hasPhone: filters.hasPhone || undefined,
    hasWebsite: filters.hasWebsite || undefined,
    verified: filters.verified || undefined,
    category: category || undefined,
    minFollowers: minFollowers || undefined,
    maxFollowers: maxFollowers || undefined,
    source: source || undefined,
    reviewStatus: reviewStatus || undefined,
  }), [page, q, sortBy, sortDir, filters, category, minFollowers, maxFollowers, source, reviewStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/lead-finder/results${buildQuery(queryParams)}`);
      setRows(res.results || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      notify(err.message || "Failed to load leads", "error");
    } finally {
      setLoading(false);
    }
  }, [queryParams, notify]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 250);
    return () => clearTimeout(debounceRef.current);
  }, [load, refreshKey]);

  useEffect(() => { setPage(1); }, [q, sortBy, sortDir, filters, category, minFollowers, maxFollowers, source, reviewStatus]);

  const toggleFilter = (key) => setFilters((f) => ({ ...f, [key]: !f[key] }));

  const changeSort = (col) => {
    if (!SORT_COLUMNS[col]) return;
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return null;
    return sortDir === "asc" ? <LuChevronUp style={{ verticalAlign: "-2px" }} /> : <LuChevronDown style={{ verticalAlign: "-2px" }} />;
  };

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setReview = async (id, status) => {
    try {
      await authFetch(`/lead-finder/result/${id}/review`, {
        method: "PUT",
        body: JSON.stringify({ reviewStatus: status }),
      });
      notify(status === "qualified" ? "Lead qualified" : status === "rejected" ? "Lead rejected" : "Marked pending", "success");
      load();
    } catch (err) {
      notify(err.message || "Failed to update review", "error");
    }
  };

  const bulkReview = async (status) => {
    if (selected.size === 0) return;
    try {
      const res = await authFetch("/lead-finder/results/bulk-review", {
        method: "POST",
        body: JSON.stringify({ ids: [...selected], reviewStatus: status }),
      });
      notify(`Updated ${res.updated} lead(s) → ${status}`, "success");
      setSelected(new Set());
      load();
    } catch (err) {
      notify(err.message || "Bulk review failed", "error");
    }
  };

  const deleteLead = (id) => {
    setConfirm({
      title: "Delete lead?",
      message: "This will hide the lead from your list and block WhatsApp messages to it.",
      onConfirm: async () => {
        setConfirm(null);
        try {
          await authFetch(`/lead-finder/result/${id}`, { method: "DELETE" });
          notify("Lead deleted", "success");
          setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
          load();
        } catch (err) {
          notify(err.message || "Delete failed", "error");
        }
      },
    });
  };

  const bulkDelete = () => {
    if (selected.size === 0) return;
    setConfirm({
      title: `Delete ${selected.size} lead(s)?`,
      message: "Deleted leads are hidden from your list and will not receive WhatsApp messages.",
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await authFetch("/lead-finder/results/bulk-delete", {
            method: "POST",
            body: JSON.stringify({ ids: [...selected] }),
          });
          notify(`Deleted ${res.deleted} lead(s)`, "success");
          setSelected(new Set());
          load();
        } catch (err) {
          notify(err.message || "Bulk delete failed", "error");
        }
      },
    });
  };

  const exportLeads = async (format) => {
    const scope = selected.size > 0 ? "selected" : "all";
    const params = {
      ...queryParams,
      page: undefined,
      pageSize: undefined,
      format,
      scope,
      ids: scope === "selected" ? [...selected].join(",") : undefined,
    };
    try {
      const ext = format === "excel" ? "xls" : format;
      await downloadFile(`/lead-finder/export${buildQuery(params)}`, `leads_export.${ext}`);
      notify(`Exported as ${format.toUpperCase()}`, "success");
    } catch (err) {
      notify(err.message || "Export failed", "error");
    }
  };

  const copyField = async (field) => {
    const scopeRows = selected.size > 0 ? rows.filter((r) => selected.has(r.id)) : rows;
    const values = scopeRows.map((r) => r[field]).filter(Boolean);
    const unique = [...new Set(values)];
    if (unique.length === 0) { notify(`No ${field}s to copy`, "info"); return; }
    try {
      await navigator.clipboard.writeText(unique.join("\n"));
      notify(`Copied ${unique.length} ${field}(s)`, "success");
    } catch (_) {
      notify("Clipboard unavailable", "error");
    }
  };

  return (
    <div className="lf-card">
      <div className="lf-toolbar">
        <input
          className="lf-search-input"
          placeholder="Search username, name, email, category…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="lf-filter-pills">
          <button className={`lf-toggle-pill ${filters.hasEmail ? "active" : ""}`} onClick={() => toggleFilter("hasEmail")}>Has Email</button>
          <button className={`lf-toggle-pill ${filters.hasPhone ? "active" : ""}`} onClick={() => toggleFilter("hasPhone")}>Has Phone</button>
          <button className={`lf-toggle-pill ${filters.hasWebsite ? "active" : ""}`} onClick={() => toggleFilter("hasWebsite")}>Has Website</button>
          <button className={`lf-toggle-pill ${filters.verified ? "active" : ""}`} onClick={() => toggleFilter("verified")}>Verified</button>
        </div>
        <select className="lf-select" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
          <option value="pending">Pending review</option>
          <option value="qualified">Qualified</option>
          <option value="rejected">Rejected</option>
          <option value="">All review statuses</option>
        </select>
        <select className="lf-select" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">All Sources</option>
          <option value="google_maps">Google Maps</option>
          <option value="instagram">Instagram</option>
        </select>
        <input className="lf-select" style={{ width: 130 }} placeholder="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
        <input className="lf-select" style={{ width: 110 }} type="number" placeholder="Min followers" value={minFollowers} onChange={(e) => setMinFollowers(e.target.value)} />
        <input className="lf-select" style={{ width: 110 }} type="number" placeholder="Max followers" value={maxFollowers} onChange={(e) => setMaxFollowers(e.target.value)} />
        <select className="lf-select" value={`${sortBy}:${sortDir}`} onChange={(e) => { const [b, d] = e.target.value.split(":"); setSortBy(b); setSortDir(d); }}>
          <option value="recent:desc">Recently Scraped</option>
          <option value="followers:desc">Followers (High→Low)</option>
          <option value="followers:asc">Followers (Low→High)</option>
          <option value="name:asc">Name (A→Z)</option>
          <option value="verified:desc">Verified First</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="lf-bulk-bar">
          <span className="lf-bulk-count">{selected.size} selected</span>
          <button className="lf-btn lf-btn-primary lf-btn-sm" onClick={() => bulkReview("qualified")}>Qualify</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => bulkReview("rejected")}>Reject</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => bulkReview("pending")}>Mark Pending</button>
          <button className="lf-btn lf-btn-danger lf-btn-sm" onClick={bulkDelete}>Delete</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("csv")}>Export CSV</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("excel")}>Export Excel</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("json")}>Export JSON</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => copyField("email")}>Copy Emails</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => copyField("phone")}>Copy Phones</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => copyField("profile_url")}>Copy URLs</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {selected.size === 0 && (
        <div className="lf-bulk-bar" style={{ background: "transparent", border: "none", padding: "0 0 8px" }}>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("csv")}>Export CSV</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("excel")}>Export Excel</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={() => exportLeads("json")}>Export JSON</button>
          <span className="lf-muted" style={{ fontSize: 12.5, marginLeft: 6 }}>
            Review leads here first. Only Qualified leads appear in WhatsApp campaigns.
          </span>
        </div>
      )}

      <div className="lf-table-wrap">
        <table className="lf-table">
          <thead>
            <tr>
              <th className="no-sort" style={{ width: 36 }}>
                <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} />
              </th>
              <th className="no-sort" style={{ width: 52 }} />
              <th onClick={() => changeSort("name")}>Name / Username {sortIcon("name")}</th>
              <th className="no-sort">Email</th>
              <th className="no-sort">Phone</th>
              <th className="no-sort">Location</th>
              <th className="no-sort">Website</th>
              <th className="no-sort">Review</th>
              <th onClick={() => changeSort("verified")}>Source {sortIcon("verified")}</th>
              <th onClick={() => changeSort("recent")}>Date {sortIcon("recent")}</th>
              <th className="no-sort">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}><td colSpan={11}><div className="lf-skeleton" /></td></tr>
              ))
            ) : rows.length === 0 ? (
              <tr><td colSpan={11}><div className="lf-empty"><div className="lf-empty-icon">🔍</div>No leads found. Start a search to collect leads.</div></td></tr>
            ) : (
              rows.map((lead) => (
                <tr key={lead.id} onClick={() => onOpenLead(lead.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleSelect(lead.id)} />
                  </td>
                  <td><Avatar lead={lead} /></td>
                  <td>
                    {lead.source === "google_maps" ? (
                      <span className="lf-username">{lead.name || lead.business_name || "—"}</span>
                    ) : (
                      <>
                        <span className="lf-username">@{lead.username}</span>
                        {lead.is_verified ? <LuBadgeCheck className="lf-verified" style={{ verticalAlign: "-2px" }} /> : null}
                      </>
                    )}
                  </td>
                  <td>{lead.email || <span className="lf-muted">—</span>}</td>
                  <td>{lead.phone || <span className="lf-muted">—</span>}</td>
                  <td>{lead.city || lead.country || lead.source_location || <span className="lf-muted">—</span>}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {lead.website ? <a href={lead.website} target="_blank" rel="noreferrer">Link</a> : <span className="lf-muted">—</span>}
                  </td>
                  <td>{reviewBadge(lead.review_status)}</td>
                  <td>
                    {lead.source === "google_maps"
                      ? <span className="lf-badge lf-badge-blue">Maps</span>
                      : (lead.is_private
                          ? <span className="lf-badge lf-badge-gray">Private</span>
                          : <span className="lf-badge lf-badge-green">Public</span>)}
                  </td>
                  <td className="lf-muted">{lead.scraped_at ? String(lead.scraped_at).slice(0, 10) : "—"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="lf-row-actions">
                      {lead.review_status !== "qualified" && (
                        <button className="lf-icon-btn" title="Qualify for WhatsApp" onClick={() => setReview(lead.id, "qualified")}>
                          <LuCheck />
                        </button>
                      )}
                      {lead.review_status !== "rejected" && (
                        <button className="lf-icon-btn" title="Reject" onClick={() => setReview(lead.id, "rejected")}>
                          <LuX />
                        </button>
                      )}
                      <button className="lf-icon-btn" title="View" onClick={() => onOpenLead(lead.id)}><LuEye /></button>
                      <button className="lf-icon-btn danger" title="Delete" onClick={() => deleteLead(lead.id)}><LuTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="lf-pagination">
        <span className="lf-page-info">
          {total.toLocaleString()} lead{total !== 1 ? "s" : ""} · Page {page} of {totalPages}
        </span>
        <div className="lf-page-controls">
          <button className="lf-btn lf-btn-ghost lf-btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
          <button className="lf-btn lf-btn-ghost lf-btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel="Delete"
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default LeadsTable;
